import { useContext } from "react";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { ValueFormatterParams } from "ag-grid-community";
import {
    ChangeInValueCellRenderer,
    UpdateCampaignPlacementCellRenderer,
} from "@cell-renderers/AllCellRenderers";
import { titleCase, numericValueFormatter } from "@ts/utils";
import { customFilterOptions, mainContext } from "@ts/constants";
import { IBulkPlacementModalDisplay, IMainContext, IRecommendationsData, SubModule, ParentModule } from "@ts/common-types";
import { useSelector } from "react-redux";
import { useGetModulesData } from "@hooks/AllCustomHooks";
import BulkPlacementModal from "@/components/modals/bulk-action-modal/BulkPlacementModal";
import { onCampaignSelectionChanged } from "@/ts/performance-overview";

const PlacementRecommendations = () => {
    const {
        setPlacementRecommendationsGridApi,
        placementRecommendationsLoading,
    } = useContext<IMainContext>(mainContext);

    const placementRecommendationData = useSelector(
        (state: IRecommendationsData) => state.placementRecommendationData.value
    );
    const bulkPlacementModalDisplay = useSelector(
        (state: IBulkPlacementModalDisplay) =>
            state.bulkPlacementModalDisplay.value
    );

    const gridParams = useGetModulesData({
        type: "grid-params",
        activeModuleName: SubModule.CAMPAIGN_PLACEMENT,
        activeParentModuleName: ParentModule.CAMPAIGN_RECOMMENDATIONS,
    });

    const placementColumnDefs = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "campaign_name",
                headerName: "Campaign Name",
                minWidth: 300,
                maxWidth: 450,
                filter: "agTextColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["text"],
                },
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                tooltipField: "custom_search_term",
                valueFormatter: (params: ValueFormatterParams) =>
                    titleCase(params.value || ""),
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                },
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: true,
            },
            {
                headerName: "Top of Search",
                field: "top_of_search",
                children: [
                    {
                        field: "placement_top_impression",
                        headerName: "Impressions",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_top_spend",
                        headerName: "Spends",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_top_sale",
                        headerName: "Sales",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_top_orders",
                        headerName: "Orders",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_top_click",
                        headerName: "Clicks",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_top_ctr",
                        headerName: "CTR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                            decimalPlace: 1,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_top_cr",
                        headerName: "CR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                            decimalPlace: 1,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_top_cpc",
                        headerName: "CPC",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_top_cpa",
                        headerName: "CPA",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_top_acos",
                        headerName: "ACoS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                        },
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_top_roas",
                        headerName: "RoAS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_top_placement",
                        headerName: "Placement",
                        cellRenderer: UpdateCampaignPlacementCellRenderer,
                        maxWidth: 180,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                        cellEditorParams: {
                            hasPinnedColumnValue: false,
                        },
                    },
                ],
            },
            {
                headerName: "Product Page",
                field: "product_page",
                children: [
                    {
                        field: "placement_product_page_impression",
                        headerName: "Impressions",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            decimalPlace: 0,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_product_page_spend",
                        headerName: "Spend",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_product_page_sale",
                        headerName: "Sales",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_product_page_orders",
                        headerName: "Orders",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_product_page_click",
                        headerName: "Clicks",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_product_page_ctr",
                        headerName: "CTR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                            decimalPlace: 1,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_product_page_cr",
                        headerName: "CR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_product_page_cpc",
                        headerName: "CPC",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_product_page_cpa",
                        headerName: "CPA",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_product_page_acos",
                        headerName: "ACoS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                        },
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_product_page_roas",
                        headerName: "RoAS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_product_page_placement",
                        headerName: "Placement",
                        // pinnedRowCellRenderer: blankCellRenderer,
                        cellRenderer: UpdateCampaignPlacementCellRenderer,
                        maxWidth: 180,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                        cellRendererParams: {
                            hasPinnedColumnValue: false,
                        },
                    },
                ],
            },
            {
                headerName: "Rest of Search",
                field: "rest_of_search",
                children: [
                    {
                        field: "placement_rest_impression",
                        headerName: "Impressions",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            decimalPlace: 0,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_rest_spend",
                        headerName: "Spend",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_rest_sale",
                        headerName: "Sales",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_rest_orders",
                        headerName: "Orders",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_rest_click",
                        headerName: "Clicks",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_rest_ctr",
                        headerName: "CTR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                            decimalPlace: 1,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_rest_cr",
                        headerName: "CR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_rest_cpc",
                        headerName: "CPC",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_rest_cpa",
                        headerName: "CPA",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_rest_acos",
                        headerName: "ACoS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPercent: true,
                            hasPinnedColumnValue: true,
                            showChangePercent: true,
                        },
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_rest_roas",
                        headerName: "RoAS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_rest_placement",
                        headerName: "Placement",
                        cellRenderer: UpdateCampaignPlacementCellRenderer,
                        maxWidth: 180,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                        cellEditorParams: {
                            hasPinnedColumnValue: false,
                        },
                    },
                ],
            },
            {
                headerName: "Buybox",
                field: "buybox",
                children: [
                    {
                        field: "placement_buybox_impression",
                        headerName: "Impressions",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            decimalPlace: 0,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_buybox_spend",
                        headerName: "Spend",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_buybox_sale",
                        headerName: "Sales",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_buybox_orders",
                        headerName: "Orders",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_buybox_click",
                        headerName: "Clicks",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_buybox_ctr",
                        headerName: "CTR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                            decimalPlace: 1,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_buybox_cr",
                        headerName: "CR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_buybox_cpc",
                        headerName: "CPC",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_buybox_cpa",
                        headerName: "CPA",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_buybox_acos",
                        headerName: "ACoS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPercent: true,
                            hasPinnedColumnValue: true,
                            showChangePercent: true,
                        },
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_buybox_roas",
                        headerName: "RoAS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_buybox_placement",
                        headerName: "Placement",
                        cellRenderer: UpdateCampaignPlacementCellRenderer,
                        maxWidth: 180,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                        cellEditorParams: {
                            hasPinnedColumnValue: false,
                        },
                    },
                ],
            },
            {
                headerName: "Homepage",
                field: "homepage",
                children: [
                    {
                        field: "placement_homepage_impression",
                        headerName: "Impressions",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            decimalPlace: 0,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_homepage_spend",
                        headerName: "Spend",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_homepage_sale",
                        headerName: "Sales",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_homepage_orders",
                        headerName: "Orders",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_homepage_click",
                        headerName: "Clicks",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_homepage_ctr",
                        headerName: "CTR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                            decimalPlace: 1,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_homepage_cr",
                        headerName: "CR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_homepage_cpc",
                        headerName: "CPC",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_homepage_cpa",
                        headerName: "CPA",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_homepage_acos",
                        headerName: "ACoS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPercent: true,
                            hasPinnedColumnValue: true,
                            showChangePercent: true,
                        },
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_homepage_roas",
                        headerName: "RoAS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_homepage_placement",
                        headerName: "Placement",
                        cellRenderer: UpdateCampaignPlacementCellRenderer,
                        maxWidth: 180,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                        cellEditorParams: {
                            hasPinnedColumnValue: false,
                        },
                    },
                ],
            },
            {
                headerName: "Search-in Grid",
                field: "searchin_grid",
                children: [
                    {
                        field: "placement_searchingrid_impression",
                        headerName: "Impressions",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            decimalPlace: 0,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_searchingrid_spend",
                        headerName: "Spend",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_searchingrid_sale",
                        headerName: "Sales",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_searchingrid_orders",
                        headerName: "Orders",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_searchingrid_click",
                        headerName: "Clicks",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_searchingrid_ctr",
                        headerName: "CTR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                            decimalPlace: 1,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_searchingrid_cr",
                        headerName: "CR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_searchingrid_cpc",
                        headerName: "CPC",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_searchingrid_cpa",
                        headerName: "CPA",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_searchingrid_acos",
                        headerName: "ACoS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPercent: true,
                            hasPinnedColumnValue: true,
                            showChangePercent: true,
                        },
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_searchingrid_roas",
                        headerName: "RoAS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_searchingrid_placement",
                        headerName: "Placement",
                        cellRenderer: UpdateCampaignPlacementCellRenderer,
                        maxWidth: 180,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                        cellEditorParams: {
                            hasPinnedColumnValue: false,
                        },
                    },
                ],
            },
            {
                headerName: "Stockup",
                field: "stockup",
                children: [
                    {
                        field: "placement_stockup_impression",
                        headerName: "Impressions",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            decimalPlace: 0,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_stockup_spend",
                        headerName: "Spend",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_stockup_sale",
                        headerName: "Sales",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_stockup_orders",
                        headerName: "Orders",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_stockup_click",
                        headerName: "Clicks",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_stockup_ctr",
                        headerName: "CTR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                            decimalPlace: 1,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_stockup_cr",
                        headerName: "CR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_stockup_cpc",
                        headerName: "CPC",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_stockup_cpa",
                        headerName: "CPA",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_stockup_acos",
                        headerName: "ACoS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPercent: true,
                            hasPinnedColumnValue: true,
                            showChangePercent: true,
                        },
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "placement_stockup_roas",
                        headerName: "RoAS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "placement_stockup_placement",
                        headerName: "Placement",
                        cellRenderer: UpdateCampaignPlacementCellRenderer,
                        maxWidth: 180,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                        cellEditorParams: {
                            hasPinnedColumnValue: false,
                        },
                    },
                ],
            },
            {
                headerName: "Total",
                field: "total",
                children: [
                    {
                        field: "impression",
                        headerName: "Impressions",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            decimalPlace: 0,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "spend",
                        headerName: "Spend",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        sort: "desc",
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "sale",
                        headerName: "Sales",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "orders",
                        headerName: "Orders",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "click",
                        headerName: "Clicks",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                            decimalPlace: 0,
                        },
                        hide: true,
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "ctr",
                        headerName: "CTR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                            decimalPlace: 1,
                        },
                        hide: true,
                    },
                    {
                        field: "cr",
                        headerName: "CR",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "cpc",
                        headerName: "CPC",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "cpa",
                        headerName: "CPA",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                    {
                        field: "acos",
                        headerName: "ACoS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: true,
                            showChangePercent: true,
                        },
                        filter: "agNumberColumnFilter",
                        filterParams: {
                            filterOptions: customFilterOptions["number"],
                        },
                    },
                    {
                        field: "roas",
                        headerName: "RoAS",
                        cellRenderer: ChangeInValueCellRenderer,
                        valueFormatter: numericValueFormatter,
                        cellRendererParams: {
                            hasPinnedColumnValue: true,
                            hasPercent: false,
                            showChangePercent: true,
                        },
                        hide: true,
                    },
                ],
            },
        ],
    });

    return (
        <div className="wrapper" style={{ flex: "1 1 0" }}>
            <div
                id="budget-grid"
                className="ag-theme-alpine"
                style={{ height: "100%", width: "100%" }}
            >
                <AgGridLayout
                    rowData={placementRecommendationData}
                    columnDefs={placementColumnDefs}
                    setGridApi={setPlacementRecommendationsGridApi}
                    loading={placementRecommendationsLoading}
                    isTotalRowsReq={true}
                    onSelectionChanged={onCampaignSelectionChanged}
                    rowHeight={gridParams?.rowHeight}
                />
            </div>
            {bulkPlacementModalDisplay &&<BulkPlacementModal/>}
        </div>
    );
};

export default PlacementRecommendations;
